<script setup lang="ts">
interface ScrollDownProps {
  text?: string
  align?: 'left' | 'right' | 'center'
  hideOnMobile?: boolean
  color?: 'black' | 'white'
}

withDefaults(defineProps<ScrollDownProps>(), {
  text: 'Scroll',
  align: 'left',
  hideOnMobile: false,
  color: 'black'
})

const isVisible = ref(false)
const isDisabled = ref(false)

const { y: scrollY } = useScroll(import.meta.client ? window : null, {
  onScroll: () => {
    if (scrollY.value > window.innerHeight / 2) {
      isDisabled.value = true
    }
  }
})

onMounted(() => {
  useTimeoutFn(() => {
    isVisible.value = true
  }, 1000)
})
</script>

<template>
  <Transition name="fade">
    <div
      v-if="!isDisabled"
      class="scroll-indicator absolute bottom-4 flex items-end gap-4 text-sm lg:bottom-5"
      :class="[
        align === 'right'
          ? 'right-4 h-12 flex-row-reverse lg:left-auto lg:right-10'
          : align === 'center'
            ? 'left-1/2 h-24 -translate-x-1/2 transform flex-col items-center lg:left-1/2'
            : 'left-4 h-12 lg:left-10',
        {
          'hidden lg:flex': hideOnMobile
        }
      ]"
    >
      <div
        class="scroll relative w-px transform-gpu overflow-hidden bg-inherit bg-opacity-10 transition-opacity duration-500 ease-quart-out"
        :class="[isVisible ? 'opacity-100' : 'opacity-0']"
      >
        <div
          class="scroll-line h-full w-full"
          :class="[
            isVisible ? '[animation-play-state:running]' : '[animation-play-state:paused]',
            color === 'black' ? 'scroll-line-black' : 'scroll-line-white'
          ]"
        ></div>
      </div>
      <span class="inline-block overflow-hidden"
        ><span
          class="inline-flex transform-gpu transition-transform duration-700 ease-quart-out"
          :class="[
            isVisible ? 'translate-y-0' : 'translate-y-[110%]',
            color === 'black' ? 'text-black' : 'text-white'
          ]"
          >{{ text }}</span
        ></span
      >
    </div>
  </Transition>
</template>

<style lang="postcss">
.scroll-indicator {
  --line-height: 48px;
  --line-height-abs: -48px;

  .scroll {
    height: var(--line-height);

    .scroll-line {
      background: linear-gradient(to bottom, black 50%, rgba(255, 255, 255, 0) 50%);
      background-position: 0 var(--line-height-abs);
      background-size: 100% 200%;

      animation-name: scrollIndicator;
      animation-timing-function: cubic-bezier(0.76, 0, 0.3, 1);
      animation-iteration-count: infinite;
      animation-duration: 2s;
      animation-fill-mode: forwards;
    }

    .scroll-line-white {
      background-image: linear-gradient(
        to bottom,
        white 50%,
        rgba(255, 255, 255, 0) 50%
      ) !important;
    }
  }
}

@keyframes scrollIndicator {
  0% {
    background-position: 0 var(--line-height-abs);
  }
  75% {
    background-position: 0 0;
  }
  100% {
    background-position: 0 var(--line-height);
  }
}
</style>
